/**
 * recommendationOutcomesService.ts
 * P4A Phase 6: Client-side service for fetching recommendation outcome data.
 *
 * Calls GET /lender/recommendation-outcomes to retrieve aggregate
 * outcome metrics (deltas, confidence, drift) per recommendation.
 *
 * Privacy: No member PII. Only segment-level aggregates. Tenant-scoped.
 * Non-causal: All framing is observational.
 */

const API_BASE = import.meta.env.VITE_API_URL || '/api';
const VS_CLIENT_ID = import.meta.env.VITE_VS_CLIENT_ID;

/** Confidence level from deterministic 4-signal model */
export type ConfidenceLevel = 'HIGH' | 'MEDIUM' | 'LOW';

/** Confidence signals (all boolean) */
export interface ConfidenceSignals {
  sample_ok: boolean;
  drift_ok: boolean;
  signal_ok: boolean;
  freshness_ok: boolean;
}

/** Delta triplet for a single metric */
export interface MetricDelta {
  baseline: number | null;
  outcome: number | null;
  delta: number | null;
}

/** Single outcome row from the API */
export interface RecommendationOutcome {
  recommendation_key: string;
  segment_key: string;
  status: 'pending' | 'measured';
  baseline_snapshot_at: string;
  outcome_measured_at: string | null;
  outcome_window_days: number;
  cohort_size: number;
  deltas: {
    score: MetricDelta;
    dti: MetricDelta;
    reserves_months: MetricDelta;
    cashflow: MetricDelta;
  } | null;
  confidence: {
    level: ConfidenceLevel;
    signals: ConfidenceSignals;
  };
  noise_suppressed: boolean;
  cohort_drift_pct: number | null;
  graduated_count: number | null;
  newly_entered_count: number | null;
}

/** Full API response shape */
export interface RecommendationOutcomesResponse {
  tenant_id: string;
  outcomes: RecommendationOutcome[];
  fetched_at: string;
}

/** Lookup map: action ID → outcome */
export type OutcomeMap = Map<string, RecommendationOutcome>;

/**
 * Fetch recommendation outcomes from the API.
 * Returns null on error (non-blocking — outcome data is optional).
 */
export async function fetchRecommendationOutcomes(): Promise<{
  data: RecommendationOutcomesResponse | null;
  error: Error | null;
}> {
  try {
    if (import.meta.env.DEV) console.log('📈 [outcomesService] Fetching recommendation outcomes...');

    const response = await fetch(`${API_BASE}/verascore/lender/recommendation-outcomes`, {
      method: 'GET',
      headers: {
        'VS-Client-Id': VS_CLIENT_ID,
        'Content-Type': 'application/json',
        'x-request-purpose': 'recommendation_optimization',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      if (import.meta.env.DEV) console.warn(`[outcomesService] API returned ${response.status}`);
      return { data: null, error: new Error(`API error ${response.status}`) };
    }

    const json = await response.json();
    const result: RecommendationOutcomesResponse = json?.data || json;

    if (import.meta.env.DEV) {
      console.log(`📈 [outcomesService] Loaded ${result.outcomes?.length || 0} outcome(s)`);
    }

    return { data: result, error: null };
  } catch (err) {
    if (import.meta.env.DEV) console.warn('[outcomesService] Failed to fetch:', err);
    return { data: null, error: err instanceof Error ? err : new Error(String(err)) };
  }
}

/**
 * Build a lookup map from the API response for fast access by action ID.
 * Maps behavioral action IDs (e.g., 'behavioral-high-risk-intervention') to outcomes.
 */
export function buildOutcomeMap(
  response: RecommendationOutcomesResponse | null,
): OutcomeMap {
  const map: OutcomeMap = new Map();
  if (!response?.outcomes) return map;

  for (const outcome of response.outcomes) {
    // Store by raw recommendation_key
    map.set(outcome.recommendation_key, outcome);
    // Also store by behavioral action ID format for easy lookup
    const actionId = `behavioral-${outcome.recommendation_key.toLowerCase().replace(/_/g, '-')}`;
    map.set(actionId, outcome);
  }

  return map;
}
